import React from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Autocomplete, TextField, useTheme } from "@mui/material";

import { Practitioner, StyledPractitionerChip } from "./StyledPractitionerChip";

interface PractitionerAutocompleteProps {
  value: Practitioner[];
  onChange: (practitioners: Practitioner[]) => void;
  onBlur?: () => void;
  options: Practitioner[];
  placeholder?: string;
  loading?: boolean;
  error?: boolean;
  helperText?: string;
  "data-testid"?: string;
}

/**
 * Autocomplete component for selecting multiple practitioners with avatar chips.
 * Follows StyledSelect pattern with theme-based styling.
 *
 * @example
 * <PractitionerAutocomplete
 *   value={selectedPractitioners}
 *   onChange={setSelectedPractitioners}
 *   options={availablePractitioners}
 *   placeholder="Select practitioners"
 *   error={hasError}
 *   helperText={errorMessage}
 *   data-testid="practitioners-autocomplete"
 * />
 */
export const PractitionerAutocomplete: React.FC<PractitionerAutocompleteProps> = ({
  value,
  onChange,
  onBlur,
  options,
  placeholder,
  loading,
  error,
  helperText,
  "data-testid": dataTestId,
}) => {
  const { palette } = useTheme();

  return (
    <Autocomplete
      multiple
      options={options}
      value={value}
      onChange={(_event, newValue) => onChange(newValue)}
      onBlur={onBlur}
      loading={loading}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      getOptionLabel={option => option.name}
      popupIcon={<ExpandMoreIcon sx={{ color: palette.grey[500] }} />}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={value.length === 0 ? placeholder : ""}
          error={error}
          helperText={helperText}
          data-testid={dataTestId}
        />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <StyledPractitionerChip key={key} practitioner={option} onDelete={tagProps.onDelete} />;
        })
      }
    />
  );
};
